/**
 * LocalStorage wrapper utilities for Efficacitas
 * Provides safe get/set/remove operations with error handling
 */

const STORAGE_KEYS = {
  TASKS: 'tasks',
  PROJECTS: 'projects',
  TAGS: 'tags',
  CACHED_USER_ID: 'cached_user_id',
  MIGRATION_BACKUP: 'migration_backup',
  MIGRATION_DONE: 'migration_done_',
  PENDING_SYNC: 'pendingSync'
};

/**
 * Safely get item from localStorage
 */
export function getItem(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading from localStorage (key: ${key}):`, error);
    return defaultValue;
  }
}

/**
 * Safely set item in localStorage
 */
export function setItem(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error writing to localStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Safely remove item from localStorage
 */
export function removeItem(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Check if data should be persisted in localStorage.
 * Demo mode in production does not persist tasks/projects/tags
 * so each session starts fresh.
 */
export function shouldPersistData() {
  const isDemo = localStorage.getItem('demo_mode') === 'true';
  const isProd = !import.meta.env.DEV;
  return !(isDemo && isProd);
}

/**
 * Clear all Efficacitas data from localStorage
 */
export function clearAllData() {
  try {
    const keysToRemove = [
      STORAGE_KEYS.TASKS,
      STORAGE_KEYS.PROJECTS,
      STORAGE_KEYS.TAGS,
      STORAGE_KEYS.CACHED_USER_ID,
      STORAGE_KEYS.PENDING_SYNC
    ];

    keysToRemove.forEach(key => removeItem(key));
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
}

/**
 * Get tasks from localStorage
 */
export function getTasks() {
  if (!shouldPersistData()) return [];
  return getItem(STORAGE_KEYS.TASKS, []);
}

/**
 * Set tasks in localStorage
 */
export function setTasks(tasks) {
  if (!shouldPersistData()) return false;
  return setItem(STORAGE_KEYS.TASKS, tasks);
}

/**
 * Get projects from localStorage
 */
export function getProjects() {
  if (!shouldPersistData()) return [];
  return getItem(STORAGE_KEYS.PROJECTS, []);
}

/**
 * Set projects in localStorage
 */
export function setProjects(projects) {
  if (!shouldPersistData()) return false;
  return setItem(STORAGE_KEYS.PROJECTS, projects);
}

/**
 * Get tags from localStorage
 */
export function getTags() {
  if (!shouldPersistData()) return [];
  return getItem(STORAGE_KEYS.TAGS, []);
}

/**
 * Set tags in localStorage
 */
export function setTags(tags) {
  if (!shouldPersistData()) return false;
  return setItem(STORAGE_KEYS.TAGS, tags);
}

/**
 * Get cached user ID
 */
export function getCachedUserId() {
  return getItem(STORAGE_KEYS.CACHED_USER_ID);
}

/**
 * Set cached user ID
 */
export function setCachedUserId(userId) {
  return setItem(STORAGE_KEYS.CACHED_USER_ID, userId);
}

/**
 * Check if migration has been done for a user
 */
export function isMigrationDone(userId) {
  const key = `${STORAGE_KEYS.MIGRATION_DONE}${userId}`;
  return localStorage.getItem(key) !== null;
}

/**
 * Mark migration as done for a user
 */
export function markMigrationDone(userId, status = 'success') {
  const key = `${STORAGE_KEYS.MIGRATION_DONE}${userId}`;
  localStorage.setItem(key, status);
}

/**
 * Get migration backup
 */
export function getMigrationBackup() {
  return getItem(STORAGE_KEYS.MIGRATION_BACKUP);
}

/**
 * Set migration backup
 */
export function setMigrationBackup(backup) {
  return setItem(STORAGE_KEYS.MIGRATION_BACKUP, backup);
}

/**
 * Download data as JSON file
 */
export function downloadJSON(data, filename = 'efficacitas_backup.json') {
  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error downloading JSON:', error);
    return false;
  }
}

export { STORAGE_KEYS };
